import { Component, Input, OnDestroy, OnInit, ViewChild, Inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import {
  map,
  startWith,
  switchMap,
  finalize,
} from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SelectionModel } from '@angular/cdk/collections';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { DeadLetterMessage } from '../models/dead-letter-message';
import { AzureResource } from '../models/resource-group';
import { AzureResourceType } from '../models/AzureResourceType';
import { PlatformPortalApiService } from '../services/platform-portal-api.service';
import { MatTable } from '@angular/material/table';
import { EnvironmentService } from '../services/environment.service';
import { Environment } from '../models/environment';
import { AdoUserProfile } from '../models/ado-user-profile';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { KeyValuePair } from '../models/key-value-pair';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '../services/toast.service';
import { LoadingService } from '../services/loading.service';

@Component({
    selector: 'app-deadletter',
    templateUrl: './deadletter.component.html',
    styleUrls: ['./deadletter.component.css'],
    standalone: false
})
export class DeadletterComponent implements OnInit, OnDestroy {
  AzureResourceType = AzureResourceType;
  environment: Environment;
  pageEnabled: boolean;
  stepperOrientation: Observable<StepperOrientation>;
  serviceBusFormGroup: FormGroup;
  queueFormGroup: FormGroup;
  messagesFormGroup: FormGroup;
  availableServiceBuses: Array<AzureResource>;
  availableServiceBusQueues: Array<AzureResource>;
  displayedColumns: any[] = [];
  columnHeaders: string[] = [];
  displayedBrokerColumns: string[] = ['Key', 'Value'];
  displayedCustomColumns: string[] = ['Key', 'Value'];
  peekCount: number = 0;
  deadLetterMessages: Array<DeadLetterMessage> | undefined;
  filteredServiceBuses: Observable<Array<AzureResource>>;
  filteredServiceBusQueues: Observable<Array<AzureResource>>;
  selectedMessage: DeadLetterMessage | undefined;
  messageBrokerProperties!: KeyValuePair[];
  messageCustomProperties!: KeyValuePair[];
  messageBody: any;
  userProfile!: AdoUserProfile;
  downloadHref!: SafeHtml;
  private readonly _destroying$ = new Subject<void>();
  _selection = new SelectionModel<DeadLetterMessage>(true, []);
  @ViewChild('deadLetterStepper') stepper!: MatStepper;
  @ViewChild('messagesTable', { static: false }) messagesTable!: MatTable<any>;
  @ViewChild('peekQueueCount') countInput!: Input;

  constructor(
    @Inject(PlatformPortalApiService) private readonly azureService: PlatformPortalApiService,
    @Inject(FormBuilder) private readonly formBuilder: FormBuilder,
    @Inject(BreakpointObserver) breakpointObserver: BreakpointObserver,
    @Inject(EnvironmentService) private readonly environmentService: EnvironmentService,
    @Inject(DomSanitizer) private readonly sanitizer: DomSanitizer,
    @Inject(MatDialog) private readonly dialog: MatDialog,
    @Inject(ToastService) private readonly toast: ToastService,
    public loadingService: LoadingService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.pageEnabled =
      this.environment.featureToggles.peekDeadLetterEnabled === 'true';

    this.availableServiceBuses = new Array<AzureResource>();
    this.availableServiceBusQueues = new Array<AzureResource>();
    this.filteredServiceBuses = of(this.availableServiceBuses);
    this.filteredServiceBusQueues = of(this.availableServiceBusQueues);
    this.serviceBusFormGroup = this.formBuilder.group({
      serviceBus: ['', [Validators.required, this.serviceBusRequired()]],
    });
    this.queueFormGroup = this.formBuilder.group({
      serviceBusQueue: [
        '',
        [Validators.required, this.serviceBusQueueRequired()],
      ],
      peekCount: ['10', [Validators.required, this.peekCountRequired()]],
    });
    this.messagesFormGroup = this.formBuilder.group({
      deadLetterMessages: ['', false],
    });
    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  ngOnInit(): void {
    if (this.pageEnabled) {
      this.loadingService.show('deadletter');
      this.loadServiceBuses();
    }
  }

  selectionRequired(
    selectionOptions: any[],
    propertyName: string,
    errorMessage: string
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (typeof control.value === 'string') {
        if (!selectionOptions.some((s) => s[propertyName] === control.value)) {
          return { [errorMessage]: { value: control.value } };
        }
        control.setValue(
          selectionOptions.find((s) => s[propertyName] === control.value)
        );
      }
      return null;
    };
  }

  serviceBusRequired(): ValidatorFn {
    return this.selectionRequired(
      this.availableServiceBuses,
      'resourceName',
      'invalidServiceBus'
    );
  }

  messagesAreSelected(): boolean {
    return this._selection.selected.length > 0;
  }

  selectAllPeekedMessages(): void {
    if (this.deadLetterMessages) {
      const allSelected = this.deadLetterMessages.length === this._selection.selected.length;

      if (allSelected) {
        this._selection.clear();
      } else {
        this._selection.clear();
        this.deadLetterMessages.forEach(message => this._selection.select(message));
      }
      this.displayMessageDetails();
    }
  }

  serviceBusQueueRequired(): ValidatorFn {
    return this.selectionRequired(
      this.availableServiceBusQueues,
      'resourceName',
      'invalidServiceBusQueue'
    );
  }

  peekCountRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value <= 0
        ? { invalidPeekCount: { value: control.value } }
        : null;
    };
  }

  displayServiceBusName(serviceBus: AzureResource): string {
    return serviceBus.resourceName;
  }

  displayServiceBusQueueName(serviceBusQueue: AzureResource): string {
    return serviceBusQueue.resourceName;
  }

  setupFilter(
    formGroup: FormGroup,
    iteratorName: string,
    fullList: any[],
    displayPropertyName: string
  ) {
    return formGroup.valueChanges.pipe(
      startWith<any>(''),
      map((value) => {
        return typeof value === 'string' ? value : value[iteratorName];
      }),
      map((value) => {
        return value === ''
          ? fullList
          : this._filter(fullList, displayPropertyName, value);
      })
    );
  }

  loadServiceBuses() {
    this.loadingService.show('deadletter');
    this.azureService
      .getServiceBuses()
      .pipe(
        finalize(() => {
          this.loadingService.hide('deadletter');
        })
      )
      .subscribe({
        next: (serviceBuses) => {
          this.availableServiceBuses = [];
          this.availableServiceBuses.push(...serviceBuses);
          this.filteredServiceBuses = this.setupFilter(
            this.serviceBusFormGroup,
            'serviceBus',
            this.availableServiceBuses,
            'resourceName'
          );
        },
        error: () => {
          this.toast.error('Error loading Service Buses');
        },
      });

    if (this.stepper) {
      this.stepper.next();
    }
  }

  loadServiceBusQueues() {
    if (this.serviceBusFormGroup.valid) {
      this.loadingService.show('deadletter');
      this.azureService
        .getServiceBusQueues(this.serviceBusFormGroup.get('serviceBus')!.value)
        .pipe(
          finalize(() => {this.loadingService.hide('deadletter')})
        )
        .subscribe({
          next: (serviceBusQueues) => {
            this.availableServiceBusQueues = [];
            this.availableServiceBusQueues.push(...serviceBusQueues);
            this.filteredServiceBusQueues = this.setupFilter(
              this.queueFormGroup,
              'serviceBusQueue',
              this.availableServiceBusQueues,
              'resourceName'
            );
          },
          error: () => {
            this.toast.error('Error loading queues');
          },
        });

      this.stepper.next();
    }
  }

  goBackToQueues() {
    this.deadLetterMessages = undefined;
    this.selectedMessage = undefined;
    this.stepper.previous();
  }

  loadDeadLetterMessages(): void {
    if (this.queueFormGroup.valid) {
      this.loadingService.show('deadletter');
      let selectedQueue = this.queueFormGroup.get('serviceBusQueue')!.value;
      let count = this.queueFormGroup.get('peekCount')!.value;
      this.deadLetterMessages = new Array<DeadLetterMessage>();
      this.azureService.getDeadLetterMessages(selectedQueue, count)
      .pipe(finalize(() => {this.loadingService.hide('deadletter')}))
      .subscribe({
        next: (messages) => {
          this.populateMessageTable(messages, count);
        },
        error: () => {
          this.toast.error('Error loading messages from queue');
        },
      });
      this.stepper.next();
    }
  }

  populateMessageTable(messages: any, count: number) {
    this._selection.clear();
    this.deadLetterMessages = new Array<DeadLetterMessage>();
    
    let messagesToProcess = messages;
    
    // If messages is a string (serialized JSON), parse it
    if (typeof messages === 'string') {
        try {
            messagesToProcess = JSON.parse(messages);
        } catch (e) {
            console.error('Failed to parse messages:', e);
            this.toast.error('Failed to parse messages');
            messagesToProcess = null; // Set to null to prevent processing invalid JSON
        }
    }
    
    // Only process messages if we have valid data
    if (messagesToProcess !== null) {
        // Ensure messages is treated as an array
        const messageArray = Array.isArray(messagesToProcess) ? messagesToProcess : [messagesToProcess];
        
        this.deadLetterMessages.push(...messageArray);
        
        this.displayedColumns = this.getColumnDefinitions();
        this.columnHeaders = this.getDisplayedColumns();
    }
    
    this.peekCount = count;
    
    // Force change detection and re-render
    this.messagesTable.renderRows();
  }

  getDisplayedColumns(): any[] {
    return [
      'select',
      'sequenceNumber',
      'messageId',
      'enqueuedTime',
      'deliveryCount',
      'subject',
      'messageRawBody',
      'deadLetterReason',
      'deadLetterErrorDescription'
    ];
  }

  getColumnDefinitions(): any[] {
    return [
      {
        columnDef: 'sequenceNumber',
        header: 'Sequence Number',
        cell: (message: DeadLetterMessage) =>
          `${message.receivedMessage.sequenceNumber}`,
      },
      {
        columnDef: 'messageId',
        header: 'Message ID',
        cell: (message: DeadLetterMessage) =>
          `${message.receivedMessage.messageId}`,
      },
      {
        columnDef: 'enqueuedTime',
        header: 'Enqueued Time',
        cell: (message: DeadLetterMessage) =>
          `${message.receivedMessage.enqueuedTime}`,
      },
      {
        columnDef: 'deliveryCount',
        header: 'Delivery Count',
        cell: (message: DeadLetterMessage) =>
          `${message.receivedMessage.deliveryCount}`,
      },
      {
        columnDef: 'subject',
        header: 'Subject',
        cell: (message: DeadLetterMessage) =>
          `${message.receivedMessage.subject}`,
      },
      {
        columnDef: 'messageRawBody',
        header: 'Message Body',
        cell: (message: DeadLetterMessage) =>
          `${message.messageRawBody.substring(0, 30)}...`,
      },
      {
        columnDef: 'deadLetterReason',
        header: 'Dead Letter Reason',
        cell: (message: DeadLetterMessage) =>
          `${message.receivedMessage.deadLetterReason}`,
      },
      {
        columnDef: 'deadLetterErrorDescription',
        header: 'Error Description',
        cell: (message: DeadLetterMessage) =>
          `${message.receivedMessage.deadLetterErrorDescription || ''}`,
      },
    ];
  }

  _filter(options: any[], propertyName: string, val: any): Array<any> {
    const filterValue: string =
      typeof val === 'string'
        ? val.toLowerCase()
        : val[propertyName].toLowerCase();

    return options.filter((option) =>
      option[propertyName].toLowerCase().includes(filterValue)
    );
  }

  exportDeadLetterMessages() {
    let messagesJson = JSON.stringify(this.deadLetterMessages);
    let uri = this.sanitizer.bypassSecurityTrustUrl(
      'data:text/json;charset=UTF-8,' + encodeURIComponent(messagesJson)
    );
    this.downloadHref = uri;
  }

  exportMessageBody() {
    let message = this.selectedMessage!.messageRawBody;
    let uri = this.sanitizer.bypassSecurityTrustUrl(
      'data:text/json;charset=UTF-8,' + encodeURIComponent(message)
    );
    this.downloadHref = uri;
  }

  processMessages(context: DeadletterComponent, requeue: boolean): void {
    let messages = context._selection.selected.map(
      (x) => x.receivedMessage.messageId
    );
    let selectedQueue = context.queueFormGroup.get('serviceBusQueue')!.value;
    let messageObservable = context.azureService.processDeadLetterMessage(selectedQueue, messages, requeue);
    context.processAndSubscribe(context, requeue, selectedQueue, messageObservable);
  }

  processAllMessages(context: DeadletterComponent, requeue: boolean): void {
    let selectedQueue = context.queueFormGroup.get('serviceBusQueue')!.value;
    let messageObservable = context.azureService.processAllMessages(selectedQueue, requeue);
    context.processAndSubscribe(context, requeue, selectedQueue, messageObservable);
  }

  processAndSubscribe(
    context: DeadletterComponent,
    requeue: boolean,
    selectedQueue: any,
    messageObservable: Observable<any>
  ): void {
    context.loadingService.show('deadletter');
    let count = context.queueFormGroup.get('peekCount')!.value;
    let queue = selectedQueue;
    messageObservable
      .pipe(
        switchMap((response) => {
          let processedMessageCount = response?.messageIds?.length ?? response.length;
          return context.azureService.getDeadLetterMessages(
            queue,
            count
          ).pipe(map((messages) => ({ messages, processedMessageCount, requeue })));
        }),
        finalize(() => {
          context.loadingService.hide('deadletter');
        })
      )
      .subscribe({
        next: ({ messages, processedMessageCount, requeue }) => {
          context.populateMessageTable(messages, count);
          let message = `${requeue ? "Requeued" : "Completed"} ${processedMessageCount} message(s)`;
          context.displaySnackbar(message);
        },
        error: () => {
          this.displaySnackbar('Error processing messages');
        },
      });
  }

  displaySnackbar(message: string): void {
    this.toast.info(message);
  }

  displayMessageDetails() {
    if (this._selection.selected.length === 0) {
      this.selectedMessage = undefined;
      return;
    }
    if (this._selection.selected.length > 1) {
      this.selectedMessage = undefined;
    } else {
      this.selectedMessage = this._selection.selected[0] ?? undefined;
    }
    
    if (this.selectedMessage) {
      let clonedObj = this.cloneObjectWithExcludedProperties(
        this.selectedMessage.receivedMessage,
        ['body', 'applicationProperties']
      );
      this.messageBrokerProperties = [];
      this.messageCustomProperties = [];
      Object.keys(clonedObj).forEach((x) => {
        this.messageBrokerProperties.push({
          Key: x,
          Value: clonedObj[x],
        });
      });
      Object.keys(
        this.selectedMessage.receivedMessage.applicationProperties
      ).forEach((x) => {
        this.messageCustomProperties.push({
          Key: x,
          Value: this.selectedMessage?.receivedMessage.applicationProperties[x],
        });
      });
    }
  }

  toggleRowSelection(row: DeadLetterMessage) {
    this._selection.toggle(row);
  }

  confirmAction(
    message: string,
    confirmActionMethod: (context: any, requeue: boolean) => void,
    requeue: boolean
  ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { confirmationText: message },
    });

    dialogRef.afterClosed().subscribe({
      next: (confirmed) => {
        if (confirmed) {
          confirmActionMethod(this, requeue);
        }
      },
      error: () => {},
    });
  }

  cloneObjectWithExcludedProperties<T, K extends keyof T>(
    original: T,
    excludedProps: K[]
  ): Omit<T, K> {
    const clonedObj = { ...original };
    excludedProps.forEach((p) => {
      delete clonedObj[p];
    });

    return clonedObj;
  }
}
